import badges from './badges.js'
import controls from './controls.js'
import * as d3 from 'd3-selection'
import { EventEmitter } from 'events'
import text from './text.js'
import Symbols from './symbol/all.js'

let count = 0

class Shape extends EventEmitter {

  constructor (container) {
    super()

    if (!container) {
      throw new Error('Shape requires a container')
    }

    this.container = container

    this.id = `shape-svg-${++count}`
  }

  setSymbol (data, dimensions) {
    if (this.symbol?.type === data.symbol) {
      // If the symbol is the same, no need to create a new one, just update the data
      this.symbol.setData(data, dimensions)
      return this
    }

    try {
      this.symbol = new Symbols[data.symbol](this.id, data)
      this.symbol.setData(data, dimensions) // Invoke setData so the symbol can set up its internal state
    } catch (e) {
      throw new Error(`Unsupported symbol type: "${data.symbol}"`)
    }

    return this
  }

  /*
   * Returns the bounding box of the shape's path.symbol elements.
   * We exclude the handles and badges from the bounding box calculation.
   *
   * The object returned has the following properties:
   * {x, y, width, height}
   *
   * This is used by canvas b/c some symbols can overflow the wob's bounding box
   */
  boundingBox () {
    const paths = this.container.querySelectorAll('svg path.symbol')
    let minX = Infinity
      , minY = Infinity
      , maxX = 0
      , maxY = 0

    // Ensure we have at least one path.symbol to calculate a bounding box
    if (!paths.length) {
      return { x: 0, y: 0, width: 0, height: 0 }
    }

    paths.forEach(path => {
      const bbox = path.getBBox()
      minX = Math.min(minX, bbox.x)
      minY = Math.min(minY, bbox.y)
      maxX = Math.max(maxX, bbox.x + bbox.width)
      maxY = Math.max(maxY, bbox.y + bbox.height)
    })

    const width = maxX - minX + this.symbol.data.strokeWidth
    const height = maxY - minY + this.symbol.data.strokeWidth

    return {
      x: minX - this.symbol.data.strokeWidth / 2,
      y: minY - this.symbol.data.strokeWidth / 2,
      width: width,
      height: height
    }
  }

  render (dimensions, sourceData, transition = false) {
    if (!sourceData.symbol) {
      throw new Error('Shape data must contain a symbol type')
    }

    let svg = d3.select(this.container).selectAll('svg')

    if (transition && svg.size() && svg.classed('is-updating')) {
      // A previous transition is still running, blow away the previous svg
      // so we can re-render without the transition.
      svg.remove()
      // Re-select the container so we can append a new svg
      svg = d3.select(this.container).selectAll('svg')
      transition = false
    }

    // Clone the data so we don't modify the original, deep cloning the handles array
    const data = Object.assign({}, sourceData, {
      handles: sourceData.handles?.map(handle => handle.slice()) || [],
      color: sourceData.color || '', // Default to an empty string to prevent issues w/ adjusting lightness
    })

    this.setSymbol(data, dimensions)

    const width = dimensions.width - data.strokeWidth
    const height = dimensions.height - data.strokeWidth

    const sourceHandles = structuredClone(data.handles) // Clone the handles for comparison after `onDimensionsChange`

    const previous = svg.data()?.[0]?.dimensions || {width, height}

    const _dimensions = Object.assign({width, height}, {
      angle: dimensions.rotate || 0,
      center: [width / 2, height / 2],
      previous: {
        width: previous.width,
        height: previous.height,
      },
      // We need to determine the dimensions `diff`, by looking at the previously bound dimensions
      // and the current dimensions. Some shapes care about the diff b/c their inner controls
      // might need to change to maintain an aspect ratio.
      diff: {
        // Percentage difference between the previous and current dimensions
        width: width / previous.width - 1,
        height: height / previous.height - 1,
      }
    })

    if (previous.width !== width || previous.height !== height) {
      // Dimensions have changed, check if any symbol needs to adjust its handles
      // based on the new dimensions
      this.symbol.onDimensionsChange(_dimensions, data.handles)
    }

    const {handles, flattenedPaths, paths} = this.symbol.getComponents(data, _dimensions)

    const isChanged = handles.some((handle, idx) => {
      if (handle.pseudo) {
        // Skip pseudo handles as they aren't stored in `data`
        return false
      }

      const [handleX, handleY] = handle.coords
      const [sourceX = 0, sourceY = 0] = sourceHandles[idx] || []
      // Return true if a change is detected, causing `some` to short-circuit
      return handleX !== sourceX || handleY !== sourceY
    })

    if (isChanged) {
      const updatedData = this.symbol.handleChangeData(handles)
      data.handles = updatedData.handles
      this.emit('change:data', updatedData)
    }

    svg = svg.data([{
      paths,
      flattenedPaths,
      handles,
      badges: data.badges,
      hoverText: data.hoverText,
      dimensions: _dimensions,
      color: data.color,
      text: data.text,
    }])

    const enter = svg.enter()
      .append('svg')
      .style('width', '100%')
      .style('height', '100%')
      .style('overflow', 'visible')
      .style('display', 'block')
    enter.append('defs')

    svg = svg.merge(enter) // Merge the enter selection with the update selection

    svg.classed('is-updating', true) // Mark the svg as updating, so we can detect if a transitions need to be cancelled
      .style('opacity', data.opacity || 1) // Apply opacity to the entire symbol, not individual paths.
      .style('transform', `translate(${data.strokeWidth / 2}px, ${data.strokeWidth / 2}px)`)
      .call(this.symbol.render.bind(this.symbol), transition)
      .call(text, this.symbol, data.strokeWidth, data.fill)
      .call(badges)
      .call(controls, this.symbol, transition)
      .attr('data-symbol', data.symbol)

    // Clear any existing listeners
    this.symbol.removeAllListeners()

    this.symbol.on('rendered', () => {
      // Mark the svg as done updating
      svg.classed('is-updating', false)
    })

    // Bind new listeners on the controls each render. Doing it this way
    // so we have access to the data and dimensions in the closure.
    this.symbol.on('handle:drag', (idx, coords) => {
      data.handles[idx] = coords
      svg.classed('is-updating', false) // Mark the svg as done updating b/c these changes are immediate, not part of a transition
      this.render(dimensions, data)
    })

    // Dispatch a `handle:dragstart` event on the shape object when a handle starts to drag
    // This is used to capture state for undo/redo
    this.symbol.on('handle:dragstart', () => {
      this.emit('handle:dragstart') // Emits to the shape object
    })

    // When drag ends, we need to publish a handle:dragend w/ the current handle state
    this.symbol.on('handle:dragend', () => {
      this.emit('handle:dragend', this.symbol.handleChangeData(handles))
    })

    return svg.node()
  }

}

export default Shape
